import { useState, useEffect, useRef, useCallback } from 'react';
import { FALLBACK_IMAGES } from '../constants/components';

interface UseImageLoaderOptions {
  threshold?: number;
  rootMargin?: string;
  fallbackSrc?: string;
  retryCount?: number;
  retryDelay?: number;
}

interface UseImageLoaderReturn {
  src: string;
  isLoading: boolean;
  hasError: boolean;
  isLoaded: boolean;
  ref: React.RefObject<HTMLImageElement | null>;
}

export const useImageLoader = (
  imageSrc: string,
  options: UseImageLoaderOptions = {},
): UseImageLoaderReturn => {
  const { fallbackSrc = FALLBACK_IMAGES.GENERIC, retryCount = 2, retryDelay = 1000 } = options;

  const [src, setSrc] = useState<string>(fallbackSrc);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const ref = useRef<HTMLImageElement>(null);

  const loadImage = useCallback(
    (imageUrl: string) => {
      if (!imageUrl) return;

      setIsLoading(true);
      setHasError(false);

      // Retry recursion lives on this local function rather than on the memoized
      // `loadImage` itself — a self-referential useCallback reads a variable
      // before it is declared, which the React Compiler rejects.
      const attempt = (url: string, attemptsLeft: number) => {
        const img = new Image();

        img.onload = () => {
          setSrc(url);
          setIsLoading(false);
          setIsLoaded(true);
          setHasError(false);
        };

        img.onerror = () => {
          setIsLoading(false);
          setHasError(true);

          if (attemptsLeft > 0) {
            setTimeout(() => {
              attempt(url, attemptsLeft - 1);
            }, retryDelay);
          } else {
            setSrc(fallbackSrc);
            setIsLoaded(true);
          }
        };

        img.src = url;
      };

      attempt(imageUrl, retryCount);
    },
    [fallbackSrc, retryCount, retryDelay],
  );

  useEffect(() => {
    if (!imageSrc) return;

    // Legitimate external-system sync: kick off browser image loading whenever
    // the source changes. The synchronous loading/error state reset here is the
    // intended "start loading" transition (and resets state on URL change), not
    // a cascading-render bug — hence the scoped rule exception.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadImage(imageSrc);
  }, [imageSrc, loadImage]);

  return {
    src,
    isLoading,
    hasError,
    isLoaded,
    ref,
  };
};
